import { IconButton, InputAdornment } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { CommentDTO } from '../../models/post'
import AvatarEditor from 'react-avatar-editor'
import { getDate, isAdmin, isCurrentUser } from '../../helpers/utils'
import { Dispatch, SetStateAction, useState } from 'react'
import SendIcon from '@mui/icons-material/Send'
import PostService from '../../services/PostService'
import { useSnackbar } from 'notistack'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CustomModal from '../../containers/Modal'
import { useNavigate } from 'react-router-dom'
import { AppRoutes } from '../../helpers/routes'
import MenuDropdown, { MenuOption } from '../../components/MenuDropdown'
import CustomInput from '../../components/TextInput'
import TextModal from '../../components/Modal/TextModal'

interface Props {
    comments: CommentDTO[]
    postId: string
    setMutate: Dispatch<SetStateAction<boolean>>
}

const SinglePostComments = ({ comments, postId, setMutate }: Props) => {
    const [comment, setComment] = useState<string>('')
    const [error, setError] = useState<string>('')
    const { enqueueSnackbar } = useSnackbar()
    const [editable, setEditable] = useState<boolean>(false)
    const [editableComment, setEditableComment] = useState<string>('')
    const [isDeleteCommentOpen, setIsDeleteCommentOpen] = useState<boolean>(false)
    const [selectedComment, setSelectedComment] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate()

    const getComments = () => {
        const reversedComments = [...comments]
        reversedComments.reverse()
        return reversedComments
    }

    const options: MenuOption[] = [
        {
            title: 'Editar',
            icon: (
                <IconButton className="pointer-events-none">
                    <EditIcon />
                </IconButton>
            ),
            action: (commentId: string | undefined) => {
                setEditable(true)
                setSelectedComment(commentId as string)
                const newComment = comments.find((c) => c.id === commentId)
                setEditableComment(newComment?.content as string)
            },
        },
        {
            title: 'Eliminar',
            icon: (
                <IconButton className="pointer-events-none">
                    <DeleteIcon />
                </IconButton>
            ),
            action: (commentId: string | undefined) => {
                setSelectedComment(commentId as string)
                setIsDeleteCommentOpen(true)
            },
        },
    ]

    const validateInfo = () => {
        const newError = comment === '' ? 'Completá este campo' : ''

        setError(newError)

        return newError === ''
    }

    const handleCommentPost = async () => {
        if (!validateInfo()) return

        setLoading(true)
        const response = await new PostService().commentPost(postId, comment)
        if (response.status === 200) {
            setMutate((prev) => !prev)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }

        setLoading(false)
        setComment('')
    }

    const handleEditComment = async () => {
        setLoading(true)
        const response = await new PostService().editCommentPost(
            postId,
            selectedComment,
            editableComment,
        )
        if (response.status === 200) {
            setMutate((prev) => !prev)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }

        setLoading(false)
        setSelectedComment('')
        setEditableComment('')
    }

    const handleDeleteComment = async () => {
        setLoading(true)
        const response = await new PostService().deleteCommentPost(postId, selectedComment)
        if (response.status === 200) {
            setMutate((prev) => !prev)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }

        setLoading(false)
        setSelectedComment('')
        setIsDeleteCommentOpen(false)
    }

    return (
        <>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    {!isAdmin() && (
                        <CustomInput
                            type="text"
                            placeholder="Dejá un comentario"
                            name="comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            errorText={error}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleCommentPost}
                                            edge="end"
                                            className="text-primary"
                                            disabled={loading}
                                        >
                                            <SendIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            multiline
                        />
                    )}
                    {getComments().map((comment) => (
                        <div
                            key={comment.id}
                            className="flex flex-col mt-2 gap-4 bg-light rounded-[20px] p-2 shadow box-border -:w-full"
                        >
                            <div className="flex flex-col">
                                <div className="flex justify-between">
                                    <div className="flex gap-4 items-center">
                                        <div
                                            onClick={() =>
                                                navigate(
                                                    AppRoutes.user.replace(
                                                        ':userId',
                                                        comment.user.id,
                                                    ),
                                                )
                                            }
                                            className="cursor-pointer"
                                        >
                                            <AvatarEditor
                                                image={
                                                    !!comment.user.picture
                                                        ? comment.user.picture
                                                        : '/adminPhoto.png'
                                                }
                                                height={40}
                                                width={40}
                                                border={0}
                                                className="z-1 rounded-full pointer-events-none"
                                            />
                                        </div>
                                        <div className="flex flex-col">
                                            <span
                                                onClick={() =>
                                                    navigate(
                                                        AppRoutes.user.replace(
                                                            ':userId',
                                                            comment.user.id,
                                                        ),
                                                    )
                                                }
                                                className="text-sm cursor-pointer hover:underline"
                                            >
                                                {comment.user.firstName +
                                                    ' ' +
                                                    comment.user.lastName}
                                            </span>
                                            <span className="text-[10px]">
                                                {getDate(comment.dateCreated)}
                                            </span>
                                        </div>
                                    </div>
                                    {isCurrentUser(comment.user) && (
                                        <MenuDropdown
                                            options={options}
                                            selectedItem={comment.id}
                                        />
                                    )}
                                </div>
                                {editable && comment.id === selectedComment ? (
                                    <CustomInput
                                        value={editableComment}
                                        onChange={(e) => setEditableComment(e.target.value)}
                                        className="bg:primary"
                                        iconButtons={[
                                            {
                                                icon: <ClearIcon />,
                                                action: () => {
                                                    setEditable(false)
                                                    setSelectedComment('')
                                                },
                                            },
                                            {
                                                icon: <SendIcon />,
                                                action: handleEditComment,
                                                loading: loading,
                                            },
                                        ]}
                                        multiline
                                    />
                                ) : (
                                    <span className="p-2 whitespace-pre-line">
                                        {comment.content}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <CustomModal
                isOpen={isDeleteCommentOpen}
                handleClose={() => setIsDeleteCommentOpen(false)}
            >
                <TextModal
                    title={'Eliminar Comentario'}
                    content={'¿Estás seguro de que querés eliminar este comentario?'}
                    onAction={handleDeleteComment}
                    onClose={() => setIsDeleteCommentOpen(false)}
                    loading={loading}
                />
            </CustomModal>
        </>
    )
}

export default SinglePostComments
